import type { Dispatch, FC, SetStateAction } from "react";
import { useState } from "react";

import { Button } from "@similarweb/ui-components";

import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { translate } from "app/filters/i18nFilter";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";

import { ButtonWrapper } from "./StyledComponents";
import type { WebsitePageResults } from "./types";
import { trackAutocompleteOpen, trackAutocompleteSelectSite, updateUrlWithCompetitors } from "./utils";

const MAX_ALLOWED_COMPETITORS = 2;

interface IQueryBarCompareButton {
    competitorsCount: number;
    similarSites: ISimilarInfoItem[];
    sitesToExclude: IChosenItem[];
    updateCompetitors: Dispatch<SetStateAction<WebsitePageResults>>;
}

export const QueryBarCompareButton: FC<IQueryBarCompareButton> = ({
    competitorsCount,
    similarSites,
    sitesToExclude,
    updateCompetitors,
}) => {
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const isMaxCompetitorsSelected = competitorsCount >= MAX_ALLOWED_COMPETITORS;
    const buttonText = translate(isMaxCompetitorsSelected ? "common.clear_all" : "common.add");

    const handleOnClick = () => {
        if (isMaxCompetitorsSelected) {
            updateCompetitors([]);
            updateUrlWithCompetitors([]);
        } else {
            setIsAutocompleteOpen(true);
        }
    };

    const handleAddNewCompetitor = (competitor: IWebsitePageResult) => {
        trackAutocompleteSelectSite(competitor.name);
        updateCompetitors((currentCompetitors) => {
            const updatedCompetitors = [...currentCompetitors, competitor] as WebsitePageResults;
            setIsAutocompleteOpen(false);
            if (updatedCompetitors?.length === 1) {
                setTimeout(() => setIsAutocompleteOpen(true), 0);
            }
            updateUrlWithCompetitors(updatedCompetitors);
            return updatedCompetitors;
        });
    };

    return (
        <div
            style={{ position: "relative" }}
            data-automation="inline-website-query-bar-compare-button"
        >
            {!isAutocompleteOpen ? (
                <ButtonWrapper>
                    <Button
                        type={buttonTypes.FLAT}
                        onClick={handleOnClick}
                    >
                        {buttonText}
                    </Button>
                </ButtonWrapper>
            ) : (
                <ClickOutsideWrapper onClickOutside={() => setIsAutocompleteOpen(false)}>
                    <AutocompleteWebsitePagesCompareItem
                        className="CompareButtonAutocompleteInlineWebsiteQueryBar"
                        similarSites={similarSites}
                        autocompleteProps={{
                            placeholder: translate("querybar.websitePages.comparebutton.autocomplete.placeholder"),
                            top: -16,
                            width: 400,
                            onFocus: () => {
                                trackAutocompleteOpen();
                            },
                        }}
                        onClick={handleAddNewCompetitor}
                        onCloseButtonClick={() => setIsAutocompleteOpen(false)}
                        excludes={sitesToExclude}
                        overrideEnterFunc={(_, item) => {
                            if (item) {
                                handleAddNewCompetitor(item as IWebsitePageResult);
                            }
                            setIsAutocompleteOpen(false);
                        }}
                    />
                </ClickOutsideWrapper>
            )}
        </div>
    );
};
